import { useEffect, useRef } from 'react';

export default function useVoiceMemoRecorder(options: {
	stream?: MediaStream;
	sampleRate: number;
	onSample: (amplitude: number) => void;
	onAmplitude: (amplitude: number) => void;
	onFinish: (blob: Blob) => void;
	onError?: (error: Error) => void;
}) {
	const ref = useRef({} as { recorder?: MediaRecorder }).current;
	const blobs = useRef<Blob[]>([]).current;

	const onAmplitude = useRef(options.onAmplitude);
	onAmplitude.current = options.onAmplitude;

	const onSample = useRef(options.onSample);
	onSample.current = options.onSample;

	const onFinish = useRef(options.onFinish);
	onFinish.current = options.onFinish;

	const onError = useRef(options.onError);
	onError.current = options.onError;

	useEffect(() => {
		if (!options.stream) return;

		blobs.length = 0;

		let context: AudioContext | undefined;
		let interval: ReturnType<typeof setInterval> | undefined;

		try {
			context = new AudioContext();

			ref.recorder = new MediaRecorder(options.stream, {
				mimeType: 'audio/webm;codecs=opus',
				audioBitsPerSecond: 40_000,
			});

			const analyser = context.createAnalyser();
			const source = context.createMediaStreamSource(options.stream);
			source.connect(analyser);

			const data = new Float32Array(analyser.fftSize);

			const avgs: number[] = [];

			interval = setInterval(() => {
				analyser.getFloatTimeDomainData(data);
				const amplitude = Math.max(...data.map(Math.abs));
				avgs.push(amplitude);
				onAmplitude.current(amplitude);
			}, 100);

			ref.recorder.ondataavailable = async (event) => {
				blobs.push(event.data);

				analyser.getFloatTimeDomainData(data);
				const sample = avgs.splice(0, avgs.length);
				onSample.current(Math.max(...sample));
			};
			ref.recorder.onstop = () => {
				onFinish.current(new Blob(blobs, { type: 'audio/webm' }));
			};
			ref.recorder.start(1000 / options.sampleRate);
		} catch (err) {
			ref.recorder = undefined;
			context?.close();
			onError.current?.(err instanceof Error ? err : new Error('Recorder failed to start'));
			return;
		}

		return () => {
			if (interval) clearInterval(interval);
			if (ref.recorder && ref.recorder.state === 'recording') {
				ref.recorder.stop();
			}
			context?.close();
		};
	}, [!!options.stream]);

	return {
		stop: () => {
			ref.recorder?.stop();
		},
	};
}
